import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  MdAdd, 
  MdEdit, 
  MdDelete, 
  MdSearch, 
  MdBusiness,
  MdPerson,
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdCheckCircle,
  MdCancel,
  MdClose,
  MdChevronRight
} from 'react-icons/md';

import api from '../config/axios';
import { useLanguage } from '../context/LanguageContext';

const SuperAdminTenantsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState(null);
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'success',
    onConfirm: null,
    showCancel: false
  });
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  // Telefon girişi kontrolü - Sadece rakam
  const handlePhoneInput = (e, setter, formData, field) => {
    const value = e.target.value;
    
    // Sadece rakamları al
    const numbers = value.replace(/\D/g, '');
    
    // Maksimum 10 rakam
    if (numbers.length > 10) {
      return;
    }
    
    // Sadece rakamları kaydet
    setter({...formData, [field]: numbers});
  };



  const [formData, setFormData] = useState({
    company_name: '',
    company_address: '',
    company_mail: '',
    company_phone: '',
    company_status: 1
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('superadmin_token');
      const response = await api.get('/superadmin/tenants', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (error) {
      console.error('Müşteriler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('superadmin_token');
      const url = editingTenant 
        ? `/superadmin/tenants/${editingTenant.company_uuid}`
        : '/superadmin/tenants';
      
      const method = editingTenant ? 'put' : 'post';
      
      await api[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowModal(false);
      setEditingTenant(null);
      resetForm();
      fetchTenants();
    } catch (error) {
      console.error('Müşteri kaydedilirken hata:', error);
    }
  };

  const handleDelete = async (id) => {
    const tenant = tenants.find(t => t.id === id);
    setTenantToDelete(tenant);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('superadmin_token');
              await api.delete(`/superadmin/tenants/${tenantToDelete.company_uuid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteModal(false);
      setTenantToDelete(null);
      fetchTenants();
      setPopup({
        show: true,
        title: t('common.success'),
        message: t('superAdmin.customerDeletedSuccessfully'),
        type: 'success',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
    } catch (error) {
      console.error('Müşteri silinirken hata:', error);
      // Hata mesajını kullanıcıya göster
      const errorMessage = error.response?.data?.message || t('superAdmin.errorOccurred');
      setPopup({
        show: true,
        title: t('common.error'),
        message: errorMessage,
        type: 'error',
        onConfirm: () => setPopup({ ...popup, show: false }),
        showCancel: false
      });
      setShowDeleteModal(false);
      setTenantToDelete(null);
    }
  };

  const handleEdit = (tenant) => {
    setEditingTenant(tenant);
    setFormData({
      company_name: tenant.company_name,
      company_address: tenant.company_address,
      company_mail: tenant.company_mail,
      company_phone: tenant.company_phone,
      company_status: tenant.company_status
    });
    setShowModal(true);
  };



  const resetForm = () => {
    setFormData({
      company_name: '',
      company_address: '',
      company_mail: '',
      company_phone: '',
      company_status: 1
    });
  };

  // Sayfa boyutu seçenekleri
  const pageSizeOptions = [10, 20, 50, 100, 500];

  // Sayfa boyutunu değiştirme fonksiyonu
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Sayfa boyutu değişince ilk sayfaya dön
  };

  // Sayfa değiştirme fonksiyonu
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const filteredTenants = tenants.filter(tenant =>
    tenant.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.company_mail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Search term değiştiğinde sayfa numarasını sıfırla
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Sayfalama hesaplamaları
  const indexOfLastTenant = currentPage * itemsPerPage;
  const indexOfFirstTenant = indexOfLastTenant - itemsPerPage;
  const currentTenants = filteredTenants.slice(indexOfFirstTenant, indexOfLastTenant);
  const totalPages = Math.ceil(filteredTenants.length / itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      


      {/* Main Content */}
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('superAdmin.customerManagement')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t('superAdmin.viewAndManageAllTenants')}
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center px-4 py-2 bg-[#272c39] text-white rounded-lg hover:bg-[#1e2230] transition-colors"
          >
            <MdAdd size={20} className="mr-2" />
            {t('superAdmin.newCustomer')}
          </button>
        </div>

        <div className="relative">
          <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder={t('superAdmin.searchCustomer')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:text-white"
          />
        </div>

        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${totalPages <= 1 ? 'rounded-b-lg' : ''}`}>
          {/* Sayfa boyutu seçici */}
          <div className="px-6 py-3 border-b border-gray-200 dark:border-[#565c66] bg-gray-50 dark:bg-gray-700 flex justify-between items-center rounded-tl-lg rounded-tr-lg">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="text-sm text-gray-600 dark:text-gray-400">entries</span>
            </div>
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full min-w-[600px] table-auto">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('superAdmin.customer')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('superAdmin.contact')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('superAdmin.status')}</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700">{t('superAdmin.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {currentTenants.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="py-4 px-4 text-center text-gray-500 dark:text-gray-400 text-sm rounded-bl-lg rounded-br-lg">
                      Müşteri bulunamadı
                    </td>
                  </tr>
                ) : (
                  currentTenants.map((tenant, index) => {
                    const isLast = index === currentTenants.length - 1 && totalPages <= 1;
                    return (
                      <tr 
                        key={tenant.id} 
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200"
                        onClick={() => navigate(`/superadmin/tenants/${tenant.company_uuid}`)}
                      >
                        <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                                <MdBusiness className="text-blue-600 dark:text-blue-300" size={20} />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {tenant.company_name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                <MdLocationOn size={14} className="inline mr-1" />
                                {tenant.company_address}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          <div className="text-sm text-gray-900 dark:text-white">
                            <div className="flex items-center mb-1">
                              <MdEmail size={14} className="mr-2 text-gray-400" />
                              {tenant.company_mail}
                            </div>
                            <div className="flex items-center">
                              <MdPhone size={14} className="mr-2 text-gray-400" />
                              {tenant.company_phone}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          <span className={`inline-flex items-center px-2 text-xs font-semibold leading-5 rounded-full ${
                            tenant.company_status 
                              ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                              : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                          }`}>
                            {tenant.company_status ? (
                              <>
                                <MdCheckCircle size={12} className="mr-1" />
                                {t('superAdmin.active')}
                              </>
                            ) : (
                              <>
                                <MdCancel size={12} className="mr-1" />
                                {t('superAdmin.inactive')}
                              </>
                            )}
                          </span>
                        </td>
                        <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right font-medium ${isLast ? 'rounded-br-lg' : ''}`}>
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(tenant);
                              }}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                              title="Düzenle"
                            >
                              <MdEdit size={16} />
                            </button>
                                                         <button
                               onClick={(e) => {
                                 e.stopPropagation();
                                 navigate(`/superadmin/tenants/${tenant.company_uuid}?showAgentModal=true`);
                               }}
                               className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                               title="Agent Ekle"
                             >
                               <MdPerson size={16} />
                             </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(tenant.id);
                              }}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              title="Sil"
                            >
                              <MdDelete size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4 p-4">
            {currentTenants.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-400 dark:text-gray-500 text-sm">Müşteri bulunamadı</p>
              </div>
            ) : (
              currentTenants.map((tenant, index) => (
                <div 
                  key={tenant.id} 
                  className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => navigate(`/superadmin/tenants/${tenant.company_uuid}`)}
                >
                  {/* Company Info & Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.customer')}</div>
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 mr-3">
                          <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                            <MdBusiness className="text-blue-600 dark:text-blue-300" size={16} />
                          </div>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{tenant.company_name}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.status')}</div>
                      <div className="text-sm text-gray-900 dark:text-white">
                        <span className={`inline-flex items-center px-2 text-xs font-semibold leading-5 rounded-full ${
                          tenant.company_status 
                            ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' 
                            : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'
                        }`}>
                          {tenant.company_status ? (
                            <>
                              <MdCheckCircle size={12} className="mr-1" />
                              {t('superAdmin.active')}
                            </>
                          ) : (
                            <>
                              <MdCancel size={12} className="mr-1" />
                              {t('superAdmin.inactive')}
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Info & Actions */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.contact')}</div>
                      <div className="text-sm text-gray-900 dark:text-white space-y-1">
                        <div className="flex items-center">
                          <MdEmail size={12} className="mr-1 text-gray-400" />
                          <span className="truncate">{tenant.company_mail}</span>
                        </div>
                        <div className="flex items-center">
                          <MdPhone size={12} className="mr-1 text-gray-400" />
                          {tenant.company_phone}
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('superAdmin.actions')}</div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(tenant);
                          }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 p-1"
                          title="Düzenle"
                        >
                          <MdEdit size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/superadmin/tenants/${tenant.company_uuid}?showAgentModal=true`);
                          }}
                          className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 p-1"
                          title="Agent Ekle"
                        >
                          <MdPerson size={16} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(tenant.id);
                          }}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1"
                          title="Sil"
                        >
                          <MdDelete size={16} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      <MdLocationOn size={12} className="inline mr-1" />
                      Address
                    </div>
                    <div className="text-sm text-gray-900 dark:text-white">{tenant.company_address}</div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6 rounded-b-lg">
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing{' '}
                    <span className="font-medium">{indexOfFirstTenant + 1}</span>
                    {' '}-{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastTenant, filteredTenants.length)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{filteredTenants.length}</span>{' '}
                    tenants
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => handlePageChange(index + 1)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                          currentPage === index + 1
                            ? 'bg-[#272c39] text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Müşteri Ekleme/Düzenleme Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center p-1 sm:p-2 lg:p-4">
          <div className="relative mx-auto w-full max-w-md sm:max-w-lg lg:w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4 flex justify-between items-center">
              <h3 className="text-sm sm:text-base lg:text-lg font-medium">
                {editingTenant ? t('superAdmin.editCustomer') : t('superAdmin.addNewCustomer')}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingTenant(null);
                  resetForm();
                }}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={16} className="sm:hidden" />
                <MdClose size={20} className="hidden sm:block lg:hidden" />
                <MdClose size={24} className="hidden lg:block" />
              </button>
            </div>
            
            <div className="p-3 sm:p-4 lg:p-8 bg-white dark:bg-gray-800">
              <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3 lg:space-y-4">
                {/* Status - EN ÜSTE */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('superAdmin.status')}
                  </label>
                  <select
                    value={formData.company_status}
                    onChange={(e) => setFormData({...formData, company_status: parseInt(e.target.value)})}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                  >
                    <option value={1}>{t('superAdmin.active')}</option>
                    <option value={0}>{t('superAdmin.inactive')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('superAdmin.companyName')}
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('superAdmin.email')}
                  </label>
                  <input
                    type="email"
                    value={formData.company_mail}
                    onChange={(e) => setFormData({...formData, company_mail: e.target.value})}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('superAdmin.phone')}
                  </label>
                  <input
                    type="tel"
                    value={formData.company_phone}
                    onChange={(e) => handlePhoneInput(e, setFormData, formData, 'company_phone')}
                    placeholder="212XXXXXXX"
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                  />
                </div>
                {/* Adres - EN ALTA, TEXTAREA */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('superAdmin.address')}
                  </label>
                  <textarea
                    value={formData.company_address}
                    onChange={(e) => setFormData({...formData, company_address: e.target.value})}
                    rows={2}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-xs sm:text-sm"
                    required
                  />
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-2 sm:mt-3 lg:mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingTenant(null);
                      resetForm();
                    }}
                    className="px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-xs sm:text-sm"
                  >
                    {t('superAdmin.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] text-xs sm:text-sm"
                  >
                    {editingTenant ? t('superAdmin.update') : t('superAdmin.add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      

      {/* Müşteri Silme Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto p-8 border w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-4 text-center">
              <h3 className="text-xl leading-6 font-medium text-gray-900 dark:text-white mb-4">
                Müşteriyi Sil
              </h3>
              <div className="mt-4 px-8 py-4">
                <p className="text-base text-gray-600 dark:text-gray-400">
                  <strong>{tenantToDelete?.company_name}</strong> müşterisini silmek istediğinizden emin misiniz?
                </p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                  Bu işlem geri alınamaz ve müşteriye ait tüm veriler silinecektir.
                </p>
              </div>
              <div className="flex justify-center gap-4 px-8 py-4 mt-2">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setTenantToDelete(null);
                  }}
                  className="px-6 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-base font-medium rounded-md shadow-sm hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none min-w-[120px]"
                >
                  İptal
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-6 py-2.5 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none min-w-[120px]"
                >
                  Evet, Sil
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Popup */}
      {popup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto p-8 border w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-4 text-center">
              <h3 className="text-xl leading-6 font-medium text-gray-900 dark:text-white mb-4">
                {popup.title}
              </h3>
              <div className="mt-4 px-8 py-4">
                <p className="text-base text-gray-600 dark:text-gray-400">
                  {popup.message}
                </p>
              </div>
              <div className="flex justify-center gap-4 px-8 py-4 mt-2">
                {popup.showCancel && (
                  <button
                    onClick={() => setPopup({ ...popup, show: false })}
                    className="px-6 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-base font-medium rounded-md shadow-sm hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none min-w-[120px]"
                  >
                    {t('superAdmin.cancel')}
                  </button>
                )}
                <button
                  onClick={popup.onConfirm}
                  className={`px-6 py-2.5 text-white text-base font-medium rounded-md shadow-sm focus:outline-none min-w-[120px] ${
                    popup.type === 'error' 
                      ? 'bg-red-600 hover:bg-red-700'
                      : popup.type === 'success'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-[#272c39] hover:bg-[#1e2230]'
                  }`}
                >
                  {t('common.ok')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && tenantToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative mx-auto p-8 border w-[500px] shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-4 text-center">
              <h3 className="text-xl leading-6 font-medium text-gray-900 dark:text-white mb-4">
                {t('superAdmin.deleteTenant')}
              </h3>
              <div className="mt-4 px-8 py-4">
                <p className="text-base text-gray-600 dark:text-gray-400">
                  {t('superAdmin.confirmDeleteTenant', { companyName: tenantToDelete.company_name })}
                </p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                  {t('superAdmin.deleteWarning')}
                </p>
              </div>
              <div className="flex justify-center gap-4 px-8 py-4 mt-2">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setTenantToDelete(null);
                  }}
                  className="px-6 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-base font-medium rounded-md shadow-sm hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none min-w-[120px]"
                >
                  {t('superAdmin.cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-6 py-2.5 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none min-w-[120px]"
                >
                  {t('superAdmin.confirmDelete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminTenantsPage;
